import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import GuideComparisonTable from './GuideComparisonTable';
import GuideSpeedFigure from './GuideSpeedFigure';
import usePageMeta from '../hooks/usePageMeta';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import { trackEvent } from '../analytics';
import { MAX_SPEED_MIN, MAX_SPEED_MAX } from '../lib/motionPace';
import './SeoGuideCluster.css';

// "How to skip the boring parts of a timelapse" (#547) — the query page in the
// guide cluster whose pillar is /guides/what-is-adaptive-pacing.
//
// WHY THIS PAGE EXISTS AND WHAT IT HAS TO GET RIGHT. The search phrase hides a
// fork that most articles about it never name: there are TWO answers, they are
// not the same thing, and they produce different videos.
//
//   1. CUT THEM OUT — the still stretches are deleted. The output is shorter,
//      and the passage of time is gone with them: a viewer cannot tell that a
//      weekend went by. In the product this is automatic stillness detection,
//      and the verb is "trim the stillness".
//   2. SPEED THROUGH THEM — the whole shoot stays, the quiet stretches just
//      play faster, so the busy ones get the screen time. Nothing cut out, and
//      the shape of the job survives. That is adaptive pacing; our
//      implementation is MotionPace.
//
// Naming the fork IS the value of the page, so the comparison table and the
// two-column choice block are the load-bearing parts, not the CTA.
//
// AUDIENCE: contractors and DIY builders. Every sentence has to survive being
// read on a phone in a truck.
//
// COPY RULES, each of them a product decision rather than style, and each with
// a regression test in SkipBoringPartsPage.test.js:
//   - THE RETIRED dead-* VOCABULARY (#379). This is the single most exposed
//     surface on the site for that slip — "dead time" is the natural phrase for
//     exactly what this page is about. The sanctioned set (lib/operationCopy.js)
//     is "stillness" / "automatic stillness detection" / "trim the stillness",
//     and otherwise "the still stretches" or "the quiet stretches".
//     src/__tests__/vocabulary.test.js fails the build on a slip; this page
//     guards it locally as well.
//   - "Nothing cut out", never "every frame kept": a paced render drops frames
//     to build the output, it just never deletes a stretch of the shoot.
//   - The brand is MotionPace, the technique is "adaptive pacing", and a video
//     made that way is "an adaptively paced timelapse" — never the noun stack,
//     never a coined category name.
//   - The speed ceiling is read from the engine, never typed in.
//   - The speed dial's default state is undecided, so the page may not claim it
//     is on or off out of the box.
//
// No runtime flag is read anywhere here: the page must render identically for a
// signed-out crawler with no JavaScript.

// The Max speed range, READ FROM THE MODULE THAT CLAMPS IT. lib/motionPace.js
// mirrors the server-side clamps in shared/motion_pace.py, so a number derived
// from it cannot outlive the limit it describes.
const MAX_SPEED_RANGE = { min: String(MAX_SPEED_MIN), max: String(MAX_SPEED_MAX) };
const MAX_SPEED_RANGE_LABEL = `${MAX_SPEED_RANGE.min}× to ${MAX_SPEED_RANGE.max}×`;

const CANONICAL_PATH = '/guides/skip-the-boring-parts';
const PAGE_TITLE = 'How to Skip the Boring Parts of a Timelapse | Tweedia Studio';
const PAGE_DESCRIPTION =
  'Two ways to skip the boring parts of a timelapse: cut the still stretches out, or speed ' +
  'through them so the busy parts get the screen time. Which one to pick, what each one costs ' +
  'you, and how to do it without watching the whole shoot first.';

// The short answer, written once and quoted everywhere: the callout at the top,
// the meta description's shape, and the first FAQ entry. A second hand-written
// version of it lower down is how a page ends up answering its own query twice.
const SHORT_ANSWER =
  'There are two ways, and they give you different videos. You can cut the still stretches out, ' +
  'which makes the video shorter but leaves nothing on screen to show how long the job took. Or ' +
  'you can speed through them, which keeps the whole shoot and hands the screen time to the ' +
  'parts where something is happening.';

const TOC = [
  { id: 'skip_two_ways', label: 'Two ways to skip the boring parts' },
  { id: 'skip_compare', label: 'Cut it out vs speed through it' },
  { id: 'skip_choose', label: 'Which one do you want?' },
  { id: 'skip_combine', label: 'Using both on one shoot' },
  { id: 'skip_manual', label: 'Doing it by hand — and where that stops working' },
  { id: 'skip_studio', label: 'How to do it in Tweedia Studio' },
  { id: 'skip_faq', label: 'FAQ' },
];

// Derived rather than hand-listed: a section added to the contents must also be
// reported to analytics, and two lists would drift.
const SECTION_IDS = TOC.map((item) => item.id);

// The fork, on the axes that actually decide it. The third row is the video you
// already have — the thing the reader is trying to get away from — so it is NOT
// highlighted; the highlight marks the two real answers, which are equals here
// rather than a recommendation.
//
// The headers live beside the data because GuideComparisonTable writes each one
// onto its own cell as `data-label`, which is what the phone layout prints above
// the value. A column renamed here is renamed in both layouts at once.
const COMPARISON_COLUMNS = [
  { key: 'label', header: 'Approach' },
  { key: 'quiet', header: 'What happens to the quiet stretches' },
  { key: 'timeReads', header: 'Can a viewer tell how long it took?' },
  { key: 'length', header: 'Output length' },
  { key: 'bestFor', header: 'Best for' },
];

const COMPARISON = [
  {
    id: 'cut',
    label: 'Cut them out',
    sub: 'trim the stillness',
    highlight: true,
    quiet: 'Deleted. They are not in the finished video at all.',
    timeReads: 'No. A rained-out week and a coffee break look the same, because both are gone.',
    length: 'Shortest. Only the stretches where something moved survive.',
    bestFor: 'Highlight reels, social posts, anything where only the work matters.',
  },
  {
    id: 'pace',
    label: 'Speed through them',
    sub: 'adaptive pacing',
    // The only linked label in this table, and so the only teal one: across the
    // cluster teal now means "this goes somewhere" and nothing else. Both rows
    // are still highlighted — the highlight is the background, not the color of
    // the label.
    to: '/guides/what-is-adaptive-pacing',
    highlight: true,
    quiet: 'Kept, and played faster — nothing cut out.',
    timeReads: 'Yes. The nights, the weekends and the wait on an inspection all still go past.',
    length: 'Short, but longer than a trim: the quiet is compressed, not removed.',
    bestFor: 'Progress videos, client updates, anything that has to show the job took time.',
  },
  {
    id: 'constant',
    label: 'Neither — one speed throughout',
    sub: 'a normal timelapse',
    quiet: 'Get exactly as much screen time as the busy ones.',
    timeReads: 'Yes, but every hour reads with the same weight.',
    length: 'Whatever your interval and frame rate happen to give you.',
    bestFor: 'Short shoots, or subjects that change steadily the whole way through.',
  },
];

// One plain-English paragraph per row, for the reader who bounced off the table.
// Keyed to the same ids so a row added without a note is caught by the tests.
const COMPARISON_NOTES = [
  {
    id: 'cut',
    title: 'Cutting them out — the video gets shorter',
    body:
      'Trimming the stillness is a delete. The stretches where nothing moved are dropped, and ' +
      'the finished video jumps from one piece of work straight to the next. That is exactly ' +
      'what you want for a highlight reel. The cost is real, though, and it is worth saying out ' +
      'loud: take the quiet out of a four-month build and it plays like a long afternoon. ' +
      'Nothing left on screen tells anyone a weekend went by.',
  },
  {
    id: 'pace',
    title: 'Speeding through them — the shoot stays whole',
    body:
      'Adaptive pacing deletes nothing. The quiet stretches stay in the video and simply play ' +
      'faster, so an empty site at 2am goes by in a second or two while the morning of the pour ' +
      'gets room to breathe. Because nothing is cut out, the shape of the job survives: the ' +
      'nights, the weekends and the week you waited on an inspection are all still there, they ' +
      'just stop costing the viewer anything. In exchange the video runs a little longer than a ' +
      'straight trim would.',
  },
  {
    id: 'constant',
    title: 'One speed throughout — the version you already have',
    body:
      'This is the normal kind of timelapse: you pick one speed up front and it never changes. ' +
      'It is also the reason you are reading this page. A fixed speed cannot tell a busy hour ' +
      'from an empty one, so the Tuesday nobody showed up gets the same slice of your video as ' +
      'the morning the crane arrived.',
  },
];

// The decision, as two lists of situations rather than a recommendation. These
// are EQUAL choices — the gc-fit columns are "pick this one" and "pick that
// one", not a good/bad split.
const CUT_IT_OUT_IF = [
  'You are cutting a highlight reel or a social post, and every second has to earn its place.',
  'The quiet stretches are genuinely worthless: a tarp over the lens, a locked gate, a week of ' +
    'solid rain with nothing behind it.',
  'The video has a hard length to hit and you are already over it.',
  'Nobody watching needs to know how long the job actually took.',
];

const SPEED_THROUGH_IF = [
  'The video is there to show progress — a client update, a monthly report, a build story.',
  'How long it took is part of the point. Cut the quiet out of a four-month job and it stops ' +
    'looking like a four-month job.',
  'The shoot has no truly worthless stretches, just slow ones you would rather not delete.',
  'You want one switch, not a timeline full of cuts to place and re-place.',
];

// THE single source for the FAQ: it renders the visible section AND feeds the
// FAQPage markup, so the two can never disagree. Plain strings on purpose — a
// rich-text answer would force a second hand-kept copy for the structured data,
// which is exactly how those drift apart.
const FAQ = [
  {
    q: 'How do you skip the boring parts of a timelapse?',
    a: SHORT_ANSWER,
  },
  {
    q: 'Should I cut the quiet stretches out or speed through them?',
    a:
      'Ask whether anyone watching needs to know how long the job took. If not, cut them out and ' +
      'take the shorter video. If that is the whole point of the video, speed through them ' +
      'instead, because a trimmed build shows the work but not the months.',
  },
  {
    q: 'Does speeding through the quiet stretches delete any footage?',
    a:
      'No — nothing cut out. The video still runs from the first frame of the shoot to the last; ' +
      'the quiet stretches just go past faster. Deleting them outright is the other approach, ' +
      'usually called trimming.',
  },
  {
    q: 'Can software find the boring parts for me?',
    a:
      'Yes. Automatic stillness detection compares each frame with the one before it and scores ' +
      'how much of the picture changed, which is how it separates a crew working from an empty ' +
      'site at 3am. You never have to watch the raw shoot to find out where the action was.',
  },
  {
    q: 'How do I skip the boring parts without losing the sense of time passing?',
    a:
      'Speed through them rather than cutting them. That is adaptive pacing: the quiet stretches ' +
      'stay in the video at a higher speed, so the elapsed time still reads on screen while the ' +
      'busy stretches keep their room.',
  },
  {
    q: 'Can I do this by hand in a video editor?',
    a:
      'Yes — either razor the still stretches out, or place speed ramps so the quiet parts run ' +
      'fast and the busy parts ease back. On a short clip that is a perfectly good answer. It ' +
      'stops being practical on a long shoot, because you have to go through the whole raw ' +
      'recording before you can place a single cut or ramp.',
  },
  {
    q: 'How much faster do the quiet stretches play?',
    a:
      `That is your call. In Tweedia Studio you set a ceiling anywhere from ${MAX_SPEED_RANGE_LABEL} ` +
      'the base speed, and the busy stretches settle back to the base speed wherever the ceiling ' +
      'sits. A low ceiling reads as a gentle drift; a high one flies.',
  },
  {
    q: 'Can I trim and pace the same shoot?',
    a:
      'As a plan, yes: drop the footage that is genuinely worthless and pace what is left. In ' +
      'Tweedia Studio the Motion section asks you to pick one of the two per edit, so the way to ' +
      'do both is to leave the worthless footage out of the project and let MotionPace handle ' +
      'the rest.',
  },
];

function buildJsonLd() {
  const url = `https://tweediastudio.com${CANONICAL_PATH}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'How to Skip the Boring Parts of a Timelapse',
        description: PAGE_DESCRIPTION,
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Tweedia Studio', url: 'https://tweediastudio.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
        image: 'https://tweediastudio.com/tweedia_tour_poster.jpg',
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function SkipBoringPartsPage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setSectionRef = useSectionViewTracking(SECTION_IDS);

  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'skip_boring_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };

  const trackNav = (target) => () => {
    trackEvent('nav_click', { target });
  };

  const studioCta = signedIn ? (
    <Link
      to="/studio"
      className="gc-primary-cta"
      data-testid="skip-boring-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </Link>
  ) : (
    <button
      type="button"
      className="gc-primary-cta"
      data-testid="skip-boring-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </button>
  );

  return (
    <div className="gc-page">
      <Header user={user} signIn={signIn} />
      <main className="gc-content" data-testid="skip-boring-content">
        <p className="gc-eyebrow">Guide</p>
        <h1>How to Skip the Boring Parts of a Timelapse</h1>
        <p className="gc-meta">
          Cut them out or speed through them — how to tell which one you want. 6 min read.
        </p>

        {/* The answer, above everything else. This block is what a search snippet
            and an answer engine quote, so it is short, complete on its own, and
            never restated in a second form further down. */}
        <div className="gc-answer" data-testid="skip-boring-answer">
          <p className="gc-answer-label">Short answer</p>
          <p className="gc-answer-text">{SHORT_ANSWER}</p>
          <p className="gc-answer-note">
            Cutting them is called <em>trimming</em>. Speeding through them is called{' '}
            <em>adaptive pacing</em>. Pick by whether the elapsed time matters.
          </p>
        </div>

        <p className="gc-lede">
          Leave a camera on a jobsite for a season and almost everything it recorded is nothing
          happening: nights, weekends, weather delays, two weeks waiting on an inspection. Nobody
          wants to watch that, and the usual fix — speed the whole thing up until it fits — flashes
          the good parts past in half a second each. This page covers the two real ways out, what
          each one costs you, and how to do either without watching the raw shoot first.
        </p>

        <nav className="gc-toc" aria-label="Table of contents">
          <p className="gc-toc-title">Contents</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="skip_two_ways"
          data-section-id="skip_two_ways"
          ref={setSectionRef('skip_two_ways')}
        >
          <h2>Two ways to skip the boring parts</h2>
          <p>
            Most articles on this treat it as one question with one answer. It is not. Two
            different things both count as skipping the boring parts, and choosing the wrong one
            gets you a video that does not do the job you made it for.
          </p>
          <p>
            <strong>Cut them out.</strong> Find the stretches where nothing moved and delete them,
            so the video runs from one piece of work to the next. Tweedia Studio does the finding
            for you with <strong>automatic stillness detection</strong> — the setting is worded as
            an instruction to trim the stillness. The result is the shortest video you can get. You
            also lose the clock: with the quiet gone, nothing on screen says whether the gap
            between two shots was ten minutes or a whole weekend.
          </p>
          <p>
            <strong>Speed through them.</strong> Keep the entire shoot, but let the quiet stretches
            play fast so the busy ones get the screen time. Nothing cut out, so the passage of time
            survives — a rained-out week is still a visible stretch of the video, it just takes
            two seconds instead of two minutes. The technique is called adaptive pacing, and if you
            want the longer version, start with{' '}
            <Link
              to="/guides/what-is-adaptive-pacing"
              data-testid="skip-boring-pillar-link"
              onClick={trackNav('adaptive_pacing_from_skip_boring')}
            >
              the plain-English definition of adaptive pacing
            </Link>
            . In Tweedia Studio it is called <strong>MotionPace</strong>.
          </p>
          <p>
            Same job, different videos. The rest of this page is about picking one.
          </p>
        </section>

        <section id="skip_compare" data-section-id="skip_compare" ref={setSectionRef('skip_compare')}>
          <h2>Cut it out vs speed through it</h2>
          <p>
            Side by side, with a normal fixed-speed timelapse in the bottom row for reference. The
            column that decides it for most people is the third one.
          </p>

          {/* Shared with the pillar and the other three guides: scrollable
              table on a desktop, one card per approach on a phone. */}
          <GuideComparisonTable
            columns={COMPARISON_COLUMNS}
            rows={COMPARISON}
            ariaLabel="Cutting the still stretches compared with speeding through them"
            testId="skip-boring-comparison"
            onRowLinkClick={() => trackEvent('nav_click', {
              target: 'adaptive_pacing_from_skip_boring_table',
            })}
          />

          {/* The argument as a picture: the still stretches shaded, one line
              ignoring them and one climbing through them. */}
          <GuideSpeedFigure variant="skipBoring" />

          {COMPARISON_NOTES.map((note) => (
            <div className="gc-note" key={note.id} data-testid={`skip-boring-note-${note.id}`}>
              <h3>{note.title}</h3>
              <p>{note.body}</p>
            </div>
          ))}
        </section>

        <section id="skip_choose" data-section-id="skip_choose" ref={setSectionRef('skip_choose')}>
          <h2>Which one do you want?</h2>
          <p>
            One question settles it nearly every time: <strong>does anyone watching need to know
            how long this took?</strong> If the answer is no, cut. If that is the whole reason the
            video exists, pace it.
          </p>
          {/* Two equal choices, not a recommendation and a warning — the columns
              are "pick this" and "pick that". */}
          <div className="gc-fit">
            <div className="gc-fit-col gc-fit-yes" data-testid="skip-boring-choice-cut">
              <h3>Cut them out if…</h3>
              <ul>
                {CUT_IT_OUT_IF.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="gc-fit-col gc-fit-alt" data-testid="skip-boring-choice-pace">
              <h3>Speed through them if…</h3>
              <ul>
                {SPEED_THROUGH_IF.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p>
            When it is genuinely a coin toss, pace it. A paced video can still be trimmed later;
            footage you cut out of a delivered video is a re-edit.
          </p>
        </section>

        <section id="skip_combine" data-section-id="skip_combine" ref={setSectionRef('skip_combine')}>
          <h2>Using both on one shoot</h2>
          <p>
            The two are not rivals, and the best result on a messy shoot usually uses both: throw
            away the footage that is worth nothing to anybody — the days the lens was tarped over,
            the week the camera faced a locked gate — and then pace everything that is left. You
            lose no time you wanted to show, and you stop paying screen time for footage that was
            never going to earn it.
          </p>
          <p>
            One practical note. In Tweedia Studio the Motion section asks you to choose one of the
            two per edit: automatic stillness detection and MotionPace are two answers to the same
            question, so they are a switch rather than a pair of checkboxes. The way to do both is
            to decide what goes into the project in the first place — leave the worthless footage
            out, then let MotionPace handle the rest of the shoot.
          </p>
        </section>

        <section id="skip_manual" data-section-id="skip_manual" ref={setSectionRef('skip_manual')}>
          <h2>Doing it by hand — and where that stops working</h2>
          <p>
            Both approaches have a manual version, and on a short clip they are perfectly good
            answers.
          </p>
          <ol className="gc-steps">
            <li>
              <strong>Scrub and cut.</strong> Drop the footage on a timeline, drag through it, and
              razor out the stretches where nothing is moving. Close the gaps and you have the
              trimmed version.
            </li>
            <li>
              <strong>Hand-placed speed ramps.</strong> Instead of cutting, tell the editor to run
              a quiet stretch fast and ease back down for the busy one, with a blend between them
              so the change does not snap. Done well, that is adaptive pacing built by hand.
            </li>
          </ol>
          <p>
            Both start with the same chore, and it is the chore that kills them: you cannot place a
            single cut or ramp until you know where the action is, and the only way to know is to
            go through the whole raw shoot. On a two-minute clip that costs you two minutes. On a
            season of a fixed jobsite camera it is tens of thousands of frames — and you do it all
            again from the top the moment you change the interval, add the next card, or re-cut
            the video. That is the point where most people quietly stop making the video.
          </p>
          <p>
            Software gets around it by measuring instead of watching: score how much changed
            between each pair of frames, and the quiet stretches identify themselves. If you are
            still working out how long to shoot for and at what interval, the{' '}
            <Link
              to="/guides/long-term-timelapse"
              data-testid="skip-boring-long-term-link"
              onClick={trackNav('long_term_guide_from_skip_boring')}
            >
              long-term timelapse guide
            </Link>{' '}
            covers that end of it. Skipping the boring parts is a decision you make after the
            footage exists, which also means it works on the card you filled last winter.
          </p>
        </section>

        <section id="skip_studio" data-section-id="skip_studio" ref={setSectionRef('skip_studio')}>
          <h2>How to do it in Tweedia Studio</h2>
          <ol className="gc-steps">
            <li>
              <strong>Upload the shoot.</strong> A video file, or a folder of interval photos —
              photos are compiled into a sequence first, then treated the same way.
            </li>
            <li>
              <strong>Open the Motion section and pick your answer.</strong> One motion pass over
              the footage, two ways to use it: trim the stillness to cut the quiet stretches, or
              MotionPace to speed through them with nothing cut out.
            </li>
            <li>
              <strong>Set the ceiling, if you paced.</strong> <em>Max speed</em> is the fastest the
              quiet stretches may run — anywhere from {MAX_SPEED_RANGE_LABEL} the base speed, with
              the busy stretches settling back to the base speed wherever you put it.{' '}
              <em>Smoothness</em> decides how gradually a speed change arrives.
            </li>
            <li>
              <strong>Preview, then render.</strong> The preview updates before you spend anything,
              so trying a setting is free. Credits are charged on the finished render.
            </li>
          </ol>
          <p>
            For the whole process end to end — interval, frame rate, settings and output — see{' '}
            <Link
              to="/guides/variable-speed-timelapse"
              data-testid="skip-boring-variable-speed-link"
              onClick={trackNav('variable_speed_guide_from_skip_boring')}
            >
              how to make a variable speed timelapse
            </Link>
            .
          </p>
          <div className="gc-callout">
            <h3>See it on real footage</h3>
            <p>
              The MotionPace page charts an actual deck build — days of a fixed camera, paced
              automatically, with nothing cut out — against the same footage at one constant speed.
            </p>
            <Link
              to="/motionpace"
              className="gc-callout-cta"
              data-testid="skip-boring-motionpace-link"
              onClick={trackNav('motionpace_from_skip_boring')}
            >
              How MotionPace works →
            </Link>
          </div>
        </section>

        <section id="skip_faq" data-section-id="skip_faq" ref={setSectionRef('skip_faq')}>
          <h2>FAQ</h2>
          <div className="gc-faq" data-testid="skip-boring-faq">
            {FAQ.map((item) => (
              <div className="gc-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="gc-final-cta" data-testid="skip-boring-final-cta">
          <h2>Give the screen time to the work</h2>
          <p>
            Upload a shoot you have already filmed, choose whether to cut the quiet stretches or
            speed through them, and watch a draft before you spend a credit.
          </p>
          <div className="gc-final-cta-row">
            {studioCta}
            <Link
              to="/motionpace"
              className="gc-secondary-cta"
              data-testid="skip-boring-final-motionpace-link"
              onClick={trackNav('motionpace_from_skip_boring_cta')}
            >
              See MotionPace
            </Link>
          </div>
          <p className="gc-note-fine">
            Tuning and previewing are always free. Credits are charged on a finished render.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default SkipBoringPartsPage;
export {
  SHORT_ANSWER,
  FAQ,
  TOC,
  COMPARISON,
  COMPARISON_COLUMNS,
  COMPARISON_NOTES,
  CUT_IT_OUT_IF,
  SPEED_THROUGH_IF,
  MAX_SPEED_RANGE,
  CANONICAL_PATH,
  PAGE_TITLE,
  buildJsonLd,
};
